import { FormJourneyItem, FormJourneyItemAddressAdditionalSettings, LeadForm } from "@plura/shared/types";
import { AnimatePresence, motion } from "framer-motion";
import { useAutoFocusAfterAnimation } from "@plura/client/hooks";
import { useMemo, useRef } from "react";
import "./address-screen.css";
import { useFormContext } from "react-hook-form";
import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { cn } from "@plura/shared/utils";
import { Input } from "../../../../molecules";
import { getCountryOptions } from "../../../../molecules/PhoneNumberInput/countryData";
import Checkbox from "../../../../molecules/Checkbox";
import SearchableDropdown, { DropdownOption } from "../DropdownScreen/SearchableDropdown";

interface Props {
  id: string;
  step: FormJourneyItem
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  onContentClick?: (fieldName: string, blockId: string) => void;
  requiresGDPRConsent?: string;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

type AddressField = keyof FormJourneyItemAddressAdditionalSettings;

const FIELD_ORDER: AddressField[] = ['address_line_1', 'address_line_2', 'city', 'region', 'postal_code', 'country'];

const AddressScreen = ({ id, step, editorMode, leadForm, active, onContentClick, requiresGDPRConsent, hiddenFields, previewMode }: Props) => {
  const additionalSettings = step.additionalSettings as FormJourneyItemAddressAdditionalSettings
  const addressLine1InputRef = useRef<HTMLInputElement>(null);
  const addressLine2InputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const regionInputRef = useRef<HTMLInputElement>(null);
  const postalCodeInputRef = useRef<HTMLInputElement>(null);
  const countryDropdownRef = useRef<{ focus: () => void } | null>(null);

  const refMap: Record<AddressField, React.RefObject<{ focus: () => void } | null>> = {
    address_line_1: addressLine1InputRef,
    address_line_2: addressLine2InputRef,
    city: cityInputRef,
    region: regionInputRef,
    postal_code: postalCodeInputRef,
    country: countryDropdownRef,
  };

  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues()
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0

  const countryFieldId = `${step.blockId}_country`;
  const countryValue = form.watch(countryFieldId);
  const countryOptions: DropdownOption[] = useMemo(
    () => getCountryOptions().map((c, i) => ({ id: c.name, value: `${c.flag} ${c.name}`, order: i })),
    []
  );

  const handleContentClick = (field: 'content' | 'secondaryContent') => {
    if ((!editorMode || (editorMode && previewMode)) || !onContentClick) return
    onContentClick(field, id)
  }

  const visibleFields = FIELD_ORDER.filter(field => additionalSettings?.[field]?.visible);

  // Enter moves to the next visible field; on the last one it bubbles up to
  // the global handler and submits the step. Landing on the country field
  // opens the search-ready picker.
  const handleFieldKeyDown = (field: AddressField) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const next = visibleFields[visibleFields.indexOf(field) + 1];
    if (!next) return;
    e.preventDefault();
    e.stopPropagation();
    refMap[next]?.current?.focus();
  };

  const shouldFocus = (!editorMode || (editorMode && previewMode)) && active;
  const { wrapperRef } = useAutoFocusAfterAnimation(() => {
    const first = visibleFields[0];
    // The country picker isn't a plain input — opening a dropdown unprompted
    // on step entry would be jarring, so skip autofocus when it's first.
    if (!first || first === 'country') return null;
    return refMap[first]?.current as HTMLInputElement | null;
  }, shouldFocus);

  const hasAnyValue = !!(addressLine1InputRef.current?.value.length || addressLine2InputRef.current?.value.length || cityInputRef.current?.value.length || regionInputRef.current?.value.length || postalCodeInputRef.current?.value.length || (typeof countryValue === 'string' && countryValue.length));

  return (
    <>
      <div className={cn(
        'questionInner',
        step.contentAlignment === 'LEFT' && 'columnLayout',
        step.contentAlignment === 'RIGHT' && 'columnLayout'
      )}>
        <div>
          <h4
            onClick={() => handleContentClick('content')}
            className={
              cn(
                hasAnyValue && 'semiFade',
                'question',
              )}
          >
            <motion.span dangerouslySetInnerHTML={{
              __html: dynamicQuestionContent({
                content: step.content,
                score: currentResponseScore,
                editMode: editorMode,
                previewMode,
                hiddenFields,
                journeyItems: leadForm.journeyItems ?? [],
                userResponses,
              })
            }} />
          </h4>
          {
            step.secondaryContent &&
            <p
              onClick={() => handleContentClick('secondaryContent')}
              className={
                cn(
                  hasAnyValue && 'semiFade',
                  'caption',
                )}
            >
              <motion.span
                dangerouslySetInnerHTML={{
                  __html: dynamicQuestionContent({
                    content: step.secondaryContent,
                    score: currentResponseScore,
                    editMode: editorMode,
                    hiddenFields,
                    previewMode,
                    journeyItems: leadForm.journeyItems ?? [],
                    userResponses,
                  })
                }}
              />
            </p>
          }
        </div>
        <div>
          <motion.div ref={wrapperRef} className={cn('answerInputWrapper')}>
            <AnimatePresence initial={false}>
              <div className={'formWrapper'}>
                {
                  !!additionalSettings?.address_line_1?.visible &&
                  <motion.div className={'fieldWrapper'}>
                    <Input
                      ref={addressLine1InputRef}
                      id={`${step.blockId}_address_line_1`}
                      placeholder={`23 Willam Street${additionalSettings?.address_line_1?.required ? '*' : ''}`}
                      className={cn('answerInput', !addressLine1InputRef.current?.value.length && 'empty')}
                      autoComplete="address-line1"
                      onKeyDown={handleFieldKeyDown('address_line_1')}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </motion.div>
                }
                {
                  !!additionalSettings?.address_line_2?.visible &&
                  <motion.div className={'fieldWrapper'}>
                    <Input
                      ref={addressLine2InputRef}
                      id={`${step.blockId}_address_line_2`}
                      placeholder={`Apartment 2${additionalSettings?.address_line_2?.required ? '*' : ''}`}
                      className={cn('answerInput', !addressLine2InputRef.current?.value.length && 'empty')}
                      autoComplete="address-line2"
                      onKeyDown={handleFieldKeyDown('address_line_2')}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </motion.div>
                }
                {
                  !!additionalSettings?.city?.visible &&
                  <motion.div className={'fieldWrapper'}>
                    <Input
                      ref={cityInputRef}
                      id={`${step.blockId}_city`}
                      placeholder={`Cambridge${additionalSettings?.city?.required ? '*' : ''}`}
                      className={cn('answerInput', !cityInputRef.current?.value.length && 'empty')}
                      autoComplete="address-level2"
                      onKeyDown={handleFieldKeyDown('city')}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </motion.div>
                }
                {
                  !!additionalSettings?.region?.visible &&
                  <motion.div className={'fieldWrapper'}>
                    <Input
                      ref={regionInputRef}
                      id={`${step.blockId}_region`}
                      placeholder={`Waikato${additionalSettings?.region?.required ? '*' : ''}`}
                      className={cn('answerInput', !regionInputRef.current?.value.length && 'empty')}
                      autoComplete="address-level1"
                      onKeyDown={handleFieldKeyDown('region')}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </motion.div>
                }
                {
                  !!additionalSettings?.postal_code?.visible &&
                  <div className={'fieldWrapperRow'}>
                    <motion.div className={'fieldWrapper'}>
                      <Input
                        ref={postalCodeInputRef}
                        id={`${step.blockId}_postal_code`}
                        placeholder={`2026${additionalSettings?.postal_code?.required ? '*' : ''}`}
                        className={cn('answerInput', !postalCodeInputRef.current?.value.length && 'empty')}
                        autoComplete="postal-code"
                        onKeyDown={handleFieldKeyDown('postal_code')}
                        data-gramm="false"
                        data-gramm_editor="false"
                        data-enable-grammarly="false"
                      />
                    </motion.div>
                  </div>
                }
                {
                  !!additionalSettings?.country?.visible &&
                  <div className={'fieldWrapperRow'}>
                    <motion.div className={'fieldWrapper'}>
                      <SearchableDropdown
                        ref={countryDropdownRef}
                        id={countryFieldId}
                        className={'searchableDropdown'}
                        placeholder={`Country${additionalSettings?.country?.required ? '*' : ''}`}
                        isClearable={true}
                        advanceOnSelect={false}
                        elementType={leadForm.elementType}
                        value={typeof countryValue === 'string' && countryValue.length ? countryValue : undefined}
                        onChange={(e) => {
                          form.setValue(countryFieldId, e.currentTarget.value, { shouldValidate: true });
                        }}
                        options={countryOptions}
                      />
                    </motion.div>
                  </div>
                }
              </div>
              {
                requiresGDPRConsent === id && (
                  <div className={'consentCheckbox'}>
                    <Checkbox
                      options={[
                        {
                          value: id,
                          label: leadForm.consentMessageTitle ?? 'I consent to the collection of my personal data'
                        },
                      ]}
                      value={form.getValues('consent')}
                      id={'consent'}
                      onChange={(e) => form.setValue('consent', e.target.checked)}
                      handleOnChange={() => form.clearErrors('consent')}
                      className="answerInput"
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </div>
                )
              }
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </>
  );
};


export default AddressScreen
